import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog } from "@angular/material/dialog";
import { ConfirmationDialogComponent, ConfirmationDialogType } from "@comunicare/comunicare-dashboard-modals";
import { FileLogger } from "@comunicare/comunicare-helper";
import { IConfiguration, IDrugsHistoric, IEntitylink, INotification, NOTIFICATION_STATUS } from "@comunicare/comunicare-interfaces";
import { TranslateService } from "@ngx-translate/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AddDrugIntakeDialogComponent } from "src/app/components/patient-drugs/add-drug-intake-dialog/add-drug-intake-dialog.component";
import { TimingData } from "src/app/models/timingData.model";
import { DrugsService } from "src/app/providers/drugs.service";
import { PatientService } from "src/app/providers/patient.service";
import { SessionService } from "src/app/providers/session.service";

@Component({
  selector: "app-drug-details",
  templateUrl: "./drug-details.component.html",
  styleUrls: ["./drug-details.component.scss"],
})
export class DrugDetailsComponent implements OnInit, OnDestroy {
  public nbSection: number;
  public sections: { [pageNbr: number]: INotification[] } = {};
  public selectedSection: INotification[] = [];
  public page = 0;
  public patientConfig: IConfiguration;
  public NOTIFICATION_STATUS = NOTIFICATION_STATUS;
  public isLoading = true;
  public notifications: INotification[];

  /** Subject that emits when the component has been destroyed. */
  private onDestroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: { drug: IEntitylink; notifications?: INotification[]; patientId: string; drugHistoric: IDrugsHistoric[] },
    private sessionService: SessionService,
    private dialog: MatDialog,
    private patientService: PatientService,
    private drugsService: DrugsService,
    private translateService: TranslateService
  ) {
    this.notifications = this.data.notifications;
  }

  ngOnInit(): void {
    this.patientService
      .getPatientConfiguration(this.data.patientId)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((config) => {
        this.patientConfig = config;
        this.isLoading = false;
      });
    this.refreshPages();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private refreshIntakes() {
    this.isLoading = true;
    const entityId = this.data.drug._id;
    const drugHistoric = this.data.drugHistoric.filter((v) => v.entityId === entityId).reverse();
    this.drugsService
      .listIntake(this.data.patientId, entityId)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (notifications) => {
          const supposedIntakes = DrugsService.getSupposedIntakes(this.data.drug.entityData, drugHistoric);
          const cibledNotif = DrugsService.getCibledNotification(entityId, notifications).reverse();
          const combinedNotifs = DrugsService.combineSupposedAndRealIntakes(supposedIntakes, cibledNotif);
          this.notifications = combinedNotifs;
          this.refreshPages();
          this.isLoading = false;
        },
        (err) => {
          this.isLoading = false;
          FileLogger.error("DrugDetailsComponent", "Error while loading listIntake: ", err);
        }
      );
  }

  private refreshPages() {
    // 10 results by page
    this.nbSection = Math.ceil(this.notifications.length / 10);
    // we build section with index
    for (let index = 0; index < this.nbSection; index++) {
      const start = index * 10;
      this.sections[index] = this.notifications.slice(start, start + 10);
    }
    this.selectedSection = this.sections[this.page];
  }

  public changePage(sign: "-" | "+"): void {
    switch (sign) {
      case "-":
        if (!this.isFirstSection()) {
          this.page--;
          this.selectedSection = this.sections[this.page];
        }
        break;
      case "+":
        if (!this.isLastSection()) {
          this.page++;
          this.selectedSection = this.sections[this.page];
        }
        break;
      default:
        break;
    }
  }

  public isLastSection(): boolean {
    return this.page + 1 === this.nbSection;
  }

  public isFirstSection(): boolean {
    return this.page + 1 === 1;
  }

  public onAddScheduledIntake(notif: INotification, index: number): void {
    const time = TimingData.getMomentHour(notif.moment, this.patientConfig.settings.notificationSettings, notif.time);
    const dialogRef = this.dialog.open(AddDrugIntakeDialogComponent, {
      data: {
        patientId: this.data.patientId,
        drug: this.data.drug,
        time: time ? time : notif.time,
        moment: notif.moment,
        quantity: notif.quantity,
        allNotifications: this.notifications,
        isScheduled: true,
      },
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result?.length > 0) {
          this.refreshIntakes();
          this.selectedSection[+index] = result[0];
          this.sessionService.needRefreshDrugsData();
        }
      });
  }

  public onAddUnscheduledIntake(): void {
    const dialogRef = this.dialog.open(AddDrugIntakeDialogComponent, {
      data: {
        patientId: this.data.patientId,
        drug: this.data.drug,
        allNotifications: this.notifications,
        isScheduled: false,
      },
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result?.length > 0) {
          this.refreshIntakes();
          this.sessionService.needRefreshDrugsData();
        }
      });
  }

  public onEditIntake(notif: INotification, index: number): void {
    const dialogRef = this.dialog.open(AddDrugIntakeDialogComponent, {
      data: {
        patientId: this.data.patientId,
        drug: this.data.drug,
        time: notif.time,
        moment: notif.moment,
        quantity: notif.quantity,
        allNotifications: this.notifications,
        isScheduled: notif.unscheduledIntake ? false : true,
        status: notif.status,
        comment: notif.comment,
        administrationTime: notif.administrationTime,
        quantityTaken: notif.quantityTaken,
      },
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result?.length > 0) {
          this.refreshIntakes();
          this.selectedSection[+index] = result[0];
          this.sessionService.needRefreshDrugsData();
        }
      });
  }

  public onDeleteIntake(notif: INotification, index: number): void {
    const message = this.translateService.instant("drugIntake.confirmDeleteSupposedIntake");

    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          message,
          type: ConfirmationDialogType.CONFIRM,
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((confirm) => {
        if (confirm) {
          this.drugsService
            .cancelScheduledDrugIntake(this.data.patientId, this.data.drug, notif.time, notif.moment)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((result) => {
              if (result) {
                this.refreshIntakes();
                this.selectedSection[+index] = result;
                this.sessionService.needRefreshDrugsData();
              }
            });
        }
      });
  }
}
